import {
  ACTION_STACK_LIMIT,
  appendActionEntry,
  appendActionEntryInPlace,
  createInitialActionStackState,
} from '../actionStack';
import {
  cloneKeyPoseSkeletonSnapshot,
  cloneKeyPoseSourcePose,
} from './keyPoseStoreUtils';
import type {
  KeyPoseActionEntry,
  KeyPoseActionKind,
  KeyPoseActionSnapshot,
  KeyPoseActionStackState,
  KeyPoseIkEffectorId,
  KeyPoseIkTargetPositions,
  KeyPoseRecord,
} from './types';

export const KEY_POSE_ACTION_STACK_LIMIT = ACTION_STACK_LIMIT;

export function createInitialKeyPoseActionStackState(): KeyPoseActionStackState {
  return createInitialActionStackState<KeyPoseActionEntry>();
}

export function cloneKeyPoseRecord(record: KeyPoseRecord): KeyPoseRecord {
  return {
    ...record,
    skeletonSnapshot: cloneKeyPoseSkeletonSnapshot(
      record.skeletonSnapshot,
      record.frame,
    ),
    sourcePose: cloneKeyPoseSourcePose(record.sourcePose, record.frame),
  };
}

export function cloneKeyPoseIkTargetPositions(
  targetPositions: KeyPoseIkTargetPositions | null | undefined,
): KeyPoseIkTargetPositions | null {
  if (!targetPositions) {
    return null;
  }

  const nextTargetPositions: KeyPoseIkTargetPositions = {};
  for (const effectorId of Object.keys(
    targetPositions,
  ) as KeyPoseIkEffectorId[]) {
    const position = targetPositions[effectorId];
    if (!position) {
      continue;
    }

    nextTargetPositions[effectorId] = [position[0], position[1], position[2]];
  }

  return nextTargetPositions;
}

export function cloneKeyPoseActionSnapshot(
  snapshot: KeyPoseActionSnapshot,
): KeyPoseActionSnapshot {
  return {
    keyPose: snapshot.keyPose ? cloneKeyPoseRecord(snapshot.keyPose) : null,
    ikTargetPositions: cloneKeyPoseIkTargetPositions(
      snapshot.ikTargetPositions,
    ),
  };
}

export function createKeyPoseActionEntry(input: {
  kind: KeyPoseActionKind;
  label: string;
  referenceName: string;
  keyPoseId: string;
  before: KeyPoseActionSnapshot;
  after: KeyPoseActionSnapshot;
}): KeyPoseActionEntry {
  return {
    id: createKeyPoseActionId(),
    kind: input.kind,
    label: input.label,
    referenceName: input.referenceName,
    keyPoseId: input.keyPoseId,
    before: cloneKeyPoseActionSnapshot(input.before),
    after: cloneKeyPoseActionSnapshot(input.after),
    timestamp: Date.now(),
  };
}

export function appendKeyPoseActionEntry(
  actionStack: KeyPoseActionStackState,
  entry: KeyPoseActionEntry,
  maxLength = KEY_POSE_ACTION_STACK_LIMIT,
): KeyPoseActionStackState {
  return appendActionEntry(actionStack, entry, maxLength);
}

export function appendKeyPoseActionEntryInPlace(
  actionStack: KeyPoseActionStackState,
  entry: KeyPoseActionEntry,
  maxLength = KEY_POSE_ACTION_STACK_LIMIT,
): KeyPoseActionStackState {
  return appendActionEntryInPlace(actionStack, entry, maxLength);
}

export function areKeyPoseActionSnapshotsEqual(
  left: KeyPoseActionSnapshot,
  right: KeyPoseActionSnapshot,
) {
  return (
    getKeyPoseActionSnapshotSignature(left) ===
    getKeyPoseActionSnapshotSignature(right)
  );
}

function getKeyPoseActionSnapshotSignature(snapshot: KeyPoseActionSnapshot) {
  return JSON.stringify(buildComparableKeyPoseActionSnapshot(snapshot));
}

function buildComparableKeyPoseActionSnapshot(snapshot: KeyPoseActionSnapshot) {
  return {
    keyPose: snapshot.keyPose
      ? buildComparableKeyPoseRecord(snapshot.keyPose)
      : null,
    ikTargetPositions: buildComparableKeyPoseIkTargetPositions(
      snapshot.ikTargetPositions,
    ),
  };
}

function buildComparableKeyPoseRecord(record: KeyPoseRecord) {
  return {
    id: record.id,
    frame: record.frame,
    hidden: Boolean(record.hidden),
    stamp: record.stamp ?? 'user',
    skeletonSnapshot: {
      schemaVersion: record.skeletonSnapshot.schemaVersion,
      frame: record.skeletonSnapshot.frame,
      transformsByName: Object.fromEntries(
        Object.entries(record.skeletonSnapshot.transformsByName)
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([name, transform]) => [
            name,
            {
              position: [...transform.position],
              quaternion: [...transform.quaternion],
              scale: [...transform.scale],
            },
          ]),
      ),
    },
    sourcePose: {
      frame: record.sourcePose.frame,
      localJointRot: record.sourcePose.localJointRot.map((axisAngle) => [
        ...axisAngle,
      ]),
      rootPosition: [...record.sourcePose.rootPosition],
      smoothRoot2d: [...record.sourcePose.smoothRoot2d],
    },
  };
}

function buildComparableKeyPoseIkTargetPositions(
  targetPositions: KeyPoseIkTargetPositions | null,
) {
  if (!targetPositions) {
    return null;
  }

  return Object.fromEntries(
    Object.entries(targetPositions)
      .flatMap(([effectorId, position]) =>
        position ? ([[effectorId, [...position]]] as const) : [],
      )
      .sort(([left], [right]) => left.localeCompare(right)),
  );
}

function createKeyPoseActionId() {
  return `key-pose-action-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}
